import React from "react";
import * as Immutable from "immutable";
import trim from "lodash/trim";
import { Panel, BootstrapModalForm, Input } from "components/bootstrap";
import Icon from "components/common/Icon";
import ClipboardButton from "components/common/ClipboardButton";
import * as FormsUtils from "util/FormsUtils";
import QueryEditModeContext from "views/components/contexts/QueryEditModeContext";
import ParameterDataTypesSelect from "enterprise/parameters/components/ParameterDataTypesSelect";
import Portal from "components/common/Portal";
import TypeSpecificDeclarationFields from "./TypeSpecificDeclarationFields";
const IdentifierRegex = /^[\w]+$/;
const backdropForMode = (mode) => {
  switch (mode) {
    case "query":
      return true;
    case "widget":
      return false;
    default:
      throw new Error(`Invalid query edit mode: ${mode}`);
  }
};
export default class ParameterDeclarationForm extends React.Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.handleFormSubmit = (e) => {
      const { onSave } = this.props;
      const { parameters } = this.state;
      e.preventDefault();
      e.stopPropagation();
      const newParameters = Immutable.Map(parameters.map((p) => [p.name, p]));
      onSave(newParameters).finally(() => {
        if (this._isMounted) {
        }
      });
    };
    this._validateName = (name) => {
      if (!trim(name)) {
        return ["error", "Must not be empty."];
      }
      if (!IdentifierRegex.test(name)) {
        return ["error", "Only characters, numbers and _ allowed."];
      }
      const { existingParameters } = this.props;
      if (existingParameters && existingParameters.has(name)) {
        return ["error", "This parameter name already exists."];
      }
      return ["success"];
    };
    this._validateTitle = (title) => {
      if (!trim(title)) {
        return ["error", "Must not be empty."];
      }
      const { existingParameters } = this.props;
      if (existingParameters && existingParameters.find(({ title: existingTitle }) => trim(title) === trim(existingTitle))) {
        return ["error", "This title is already in use."];
      }
      return ["success"];
    };
    this._validateParameter = (p) => ({
      name: this._validateName(p.name),
      title: this._validateTitle(p.title)
    });
    this._validateParameters = (parameters) => parameters.map((p) => this._validateParameter(p)).toMap();
    this._validate = (parameters) => {
      const newValidationStates = this._validateParameters(parameters);
      this.setState(({ validationStates: currentValidationStates }) => ({
        validationStates: currentValidationStates.mergeDeepWith(
          (state1, state2) => ({ ...state1, ...state2 }),
          newValidationStates
        )
      }));
    };
    this.handleValidate = (idx, state) => this.setState(({ validationStates }) => ({
      validationStates: validationStates.update(idx, (current) => ({ ...current, ...state }))
    }));
    this.updateParameter = (idx, updater) => {
      const { parameters } = this.state;
      const newParameters = parameters.update(idx, updater);
      this._validate(newParameters);
      this.setState({ parameters: newParameters });
    };
    this.handleParameterField = (idx) => (e) => {
      const key = e.target.name;
      const value = FormsUtils.getValueFromInput(e.target);
      this.updateParameterField(idx, key, value);
    };
    this.updateParameterField = (idx, key, value) => this.updateParameter(idx, (parameter) => parameter.toBuilder()[key](value).build());
    const { parameters } = props;
    this.state = {
      parameters: Immutable.List(parameters.valueSeq()),
      validationStates: this._validateParameters(parameters.valueSeq())
    };
  }
  componentDidMount() {
    this._isMounted = true;
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  renderParameter(parameter, idx) {
    var _a, _b, _c, _d, _e, _f;
    const { name, title, description, type } = parameter;
    const parameterSyntax = `$${name}$`;
    const { validationStates } = this.state;
    const { allowEditingName } = this.props;
    const validationState = validationStates.get(idx);
    const onChangeEvent = this.handleParameterField(idx);
    const onChange = (key, value) => this.updateParameterField(idx, key, value);
    const onChangeParameter = (updater) => this.updateParameter(idx, updater);
    return /* @__PURE__ */ React.createElement("fieldset", { key: `fieldset-${idx}` }, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: `name-${idx}`,
        type: "text",
        name: "name",
        label: "Name",
        disabled: !allowEditingName,
        value: name,
        onChange: onChangeEvent,
        bsStyle: (_a = validationState == null ? void 0 : validationState.name) == null ? void 0 : _a[0],
        help: (_b = validationState == null ? void 0 : validationState.name) == null ? void 0 : _b[1],
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: `title-${idx}`,
        type: "text",
        name: "title",
        label: "Title",
        value: title,
        onChange: onChangeEvent,
        bsStyle: (_c = validationState == null ? void 0 : validationState.title) == null ? void 0 : _c[0],
        help: (_d = validationState == null ? void 0 : validationState.title) == null ? void 0 : _d[1],
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: `description-${idx}`,
        type: "text",
        name: "description",
        label: "Description",
        value: description,
        bsStyle: (_e = validationState == null ? void 0 : validationState.description) == null ? void 0 : _e[0],
        help: (_f = validationState == null ? void 0 : validationState.description) == null ? void 0 : _f[1],
        onChange: onChangeEvent
      }
    ), /* @__PURE__ */ React.createElement(
      ParameterDataTypesSelect,
      {
        idx,
        validationState,
        value: type,
        onChange: onChangeParameter
      }
    ), /* @__PURE__ */ React.createElement(
      TypeSpecificDeclarationFields,
      {
        idx,
        type: type != null ? type : "value-parameter-v1",
        parameter,
        onChange,
        validationState,
        onValidate: this.handleValidate
      }
    ), /* @__PURE__ */ React.createElement(Panel, { style: { marginTop: 20 } }, /* @__PURE__ */ React.createElement(Panel.Heading, null, /* @__PURE__ */ React.createElement(Panel.Title, null, "How to use")), /* @__PURE__ */ React.createElement(Panel.Body, null, "After declaring it, you can use this parameter in all queries by using the", " ", /* @__PURE__ */ React.createElement("span", { style: { whiteSpace: "nowrap" } }, /* @__PURE__ */ React.createElement("code", null, parameterSyntax), /* @__PURE__ */ React.createElement(
      ClipboardButton,
      {
        title: /* @__PURE__ */ React.createElement(Icon, { name: "content_copy" }),
        bsSize: "xsmall",
        text: parameterSyntax,
        buttonTitle: "Copy parameter to clipboard"
      }
    )), " ", "syntax in your query, whenever you want the value of the parameter to be inserted.")));
  }
  render() {
    const { parameters, validationStates } = this.state;
    const { title, onClose, show } = this.props;
    const errorStates = validationStates.filter(
      (states) => Object.values(states).find((state) => state[0] === "error") !== void 0
    );
    const submitButtonDisabled = errorStates !== void 0 && !errorStates.isEmpty();
    const parameterFields = parameters.map((parameter, idx) => this.renderParameter(parameter, idx)).valueSeq().toJS();
    const backdrop = backdropForMode(this.context);
    return /* @__PURE__ */ React.createElement(Portal, null, /* @__PURE__ */ React.createElement(
      BootstrapModalForm,
      {
        submitButtonDisabled,
        submitButtonText: "Declare parameters",
        title,
        "data-telemetry-title": "Declare parameters",
        onCancel: onClose,
        show,
        backdrop,
        onSubmitForm: this.handleFormSubmit
      },
      parameterFields
    ));
  }
}
ParameterDeclarationForm.contextType = QueryEditModeContext;
ParameterDeclarationForm.defaultProps = {
  allowEditingName: false,
  title: "Declare parameters",
  show: true
};
